import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Collapse, Grid, IconButton } from '@material-ui/core'
import { ChevronRight, Visibility, VisibilityOff } from '@material-ui/icons'
import TextInput from '../../Form/TextInput'
import HelpTooltip from '../../Tooltip/HelpTooltip'
import { useInput } from '../../../hooks/useInput'
import { sendPasswordEmail } from '../../../actions/emailActions'
import { EMAIL_PASSWORD_RESET } from '../../../constants/emailConstants'
import ToastMessage from '../../ToastMessage/ToastMessage'
import { usePasswordValidator } from '../../../hooks/usePasswordValidator'
import { updateUserPassword } from '../../../actions/userActions'
import { USER_UPDATE_PASSWORD_RESET } from '../../../constants/userConstants'
import { SIDE_NAV_CLOSE } from '../../../constants/navigationConstants'

const PasswordInformation = () => {
  const dispatch = useDispatch()

  const [expanded, setExpanded] = useState(false)
  const [emailToast, setEmailToast] = useState(false)
  const [passwordToast, setPasswordToast] = useState(false)
  const [showOldPassword, setShowOldPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const {
    value: currentPassword,
    bind: bindCurrentPassword,
    setValue: setCurrentPassword,
  } = useInput('')
  const {
    value: newPassword,
    bind: bindNewPassword,
    error: newPasswordError,
    setError: setNewPasswordError,
    setValue: setNewPassword,
  } = useInput('')
  const {
    value: confirmPassword,
    bind: bindConfirmPassword,
    setValue: setConfirmPassword,
  } = useInput('')

  const passwordIsValid = usePasswordValidator(newPassword)

  const { userInfo } = useSelector((state) => state.userLogin)
  const { loading, success } = useSelector((state) => state.emailPassword)
  const { error: passwordError, success: passwordSuccess } = useSelector(
    (state) => state.userUpdatePassword
  )

  const buttonIsReady =
    newPassword !== '' && currentPassword !== '' && confirmPassword !== ''

  useEffect(() => {
    if (success) {
      dispatch({ type: EMAIL_PASSWORD_RESET })
      dispatch({ type: SIDE_NAV_CLOSE })
      setEmailToast(true)
    }

    if (passwordSuccess) {
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      dispatch({ type: USER_UPDATE_PASSWORD_RESET })
      dispatch({ type: SIDE_NAV_CLOSE })
      setPasswordToast(true)
    }
  }, [
    dispatch,
    success,
    passwordSuccess,
    setCurrentPassword,
    setConfirmPassword,
    setNewPassword,
  ])

  const handleExpandClick = () => {
    setExpanded(!expanded)
  }

  const formIsValid = () => {
    let ready = true

    if (newPassword !== confirmPassword) {
      ready = false
      setNewPasswordError('Passwords do not match')
    } else {
      setNewPasswordError('')
    }

    if (passwordIsValid !== '') {
      ready = false
      setNewPasswordError(passwordIsValid)
    }

    return ready
  }

  const handleUpdateClick = () => {
    if (formIsValid()) {
      dispatch(updateUserPassword(currentPassword, newPassword, userInfo._id))
    }
  }

  const handleResetClick = () => {
    dispatch(sendPasswordEmail(userInfo.email))
  }

  return (
    <div className="p-t-40 p-b-40" style={{ borderBottom: '1px solid black' }}>
      <div className="flex space-between">
        <div className="flex">
          <div>
            <IconButton
              onClick={handleExpandClick}
              aria-expanded={expanded}
              aria-label="show more"
              className={`m-r-10 ${expanded ? 'rotate' : ''}`}
            >
              <ChevronRight />
            </IconButton>
          </div>
          <div>
            <h4 className="m-b-0">Password information</h4>
            <p className="m-b-0 gray-text fs14">Update your password</p>
          </div>
        </div>
      </div>
      <Collapse in={expanded} timeout="auto" unmountOnExit className="m-l-44">
        <Grid container spacing={2} className="m-t-32">
          <Grid item xs={6} className="p-b-16">
            <TextInput
              name="currentPassword"
              label="Current password"
              bindValue={bindCurrentPassword}
              error={passwordError}
              type={showOldPassword ? 'text' : 'password'}
              endAdornment={true}
              handleClick={() => setShowOldPassword(!showOldPassword)}
              icon={showOldPassword ? <VisibilityOff /> : <Visibility />}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={6} className="p-b-16">
            <TextInput
              name="newPassword"
              type={showNewPassword ? 'text' : 'password'}
              autoComplete="old-password"
              label={
                <>
                  New Password
                  <HelpTooltip
                    title={
                      <div>
                        <p className="m-b-0">Password requirements: </p>
                        <ul className="p-l-30">
                          <li>At least 8 characters</li>
                          <li>Upper and lowercase letters</li>
                          <li>Inclusion of at least one special character</li>
                        </ul>
                      </div>
                    }
                  />
                </>
              }
              bindValue={bindNewPassword}
              error={newPasswordError}
              endAdornment={true}
              handleClick={() => setShowNewPassword(!showNewPassword)}
              icon={showNewPassword ? <VisibilityOff /> : <Visibility />}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextInput
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              label="Confirm password"
              bindValue={bindConfirmPassword}
              error={newPasswordError}
              autoComplete="confirm-password"
              endAdornment={true}
              handleClick={() => setShowConfirmPassword(!showConfirmPassword)}
              icon={showConfirmPassword ? <VisibilityOff /> : <Visibility />}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={6} className="p-t-32">
            <div className="flex space-between">
              <div className="fs12">
                Forgot password?{' '}
                <span className="link" onClick={handleResetClick}>
                  Send reset link
                </span>
              </div>

              <div className="fs12">
                Forgot password?{' '}
                <span className="link" onClick={handleResetClick}>
                  Send reset link
                </span>
              </div>
            </div>
          </Grid>
          <Grid item xs={6} className="p-t-32 text-right">
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpdateClick}
              disabled={!buttonIsReady || loading}
            >
              Update
            </Button>
          </Grid>
        </Grid>
      </Collapse>
      <ToastMessage
        message={
          <>
            <p className="fs16 fw600">Password recovery sent</p>
            <p className="fs14 m-b-0">
              A password recovery email has been sent. Please check your inbox.
            </p>
          </>
        }
        open={emailToast}
        setOpen={setEmailToast}
      />
      <ToastMessage
        message={
          <>
            <p className="fs16 fw600">Success</p>
            <p className="fs14 m-b-0">
              Your password has been successfully updated.
            </p>
          </>
        }
        open={passwordToast}
        setOpen={setPasswordToast}
      />
    </div>
  )
}

export default PasswordInformation
